import React from "react";

import { useDroppable } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { FieldErrors } from "src/api/types";
import { AddConditionButton } from "src/clauseBlock/AddConditionButton";
import {
  ClauseDragPreview,
  ClauseFormValues,
  GroupDropData,
  SharedRowProps,
} from "src/clauseBlock/ClauseBlock";
import { ClauseName, ClauseRow } from "src/clauseBlock/ClauseRow";
import { getNewClause } from "src/clauseBlock/conditionGroups";
import {
  CLAUSE_BLOCK_SIZES,
  ClauseBlockSize,
  JunctionDropdown,
} from "src/clauseBlock/JunctionDropdown";
import { Clause } from "src/clients/flow-api";

type Props = SharedRowProps & {
  index: number;
  isLoneGroup: boolean;
  onDelete: () => void;
  dragPreview: ClauseDragPreview | null;
  fieldErrors?: FieldErrors;
  hideActionButtons?: boolean;
};

// A row as it should render right now: during a cross-group drag the dragged
// clause appears at its previewed position while its inputs stay bound to
// clauseName, the form path it still occupies until the drop commits the move.
type DisplayClause = {
  clause: Clause;
  clauseName: ClauseName;
  // The clause's index in this group's form array; null for the previewed
  // clause, which still lives in another group.
  formIndex: number | null;
};

const toDisplayClauses = (
  clauses: Clause[],
  groupIndex: number,
  dragPreview: ClauseDragPreview | null,
): DisplayClause[] => {
  const formClauses: DisplayClause[] = clauses.map((clause, formIndex) => ({
    clause,
    clauseName: `groups.${groupIndex}.clauses.${formIndex}` as ClauseName,
    formIndex,
  }));
  if (!dragPreview) {
    return formClauses;
  }
  if (groupIndex === dragPreview.from.groupIndex) {
    return formClauses.filter(
      ({ formIndex }) => formIndex !== dragPreview.from.clauseIndex,
    );
  }
  if (groupIndex === dragPreview.to.groupIndex) {
    // toSpliced clamps, so an index past the end appends
    return formClauses.toSpliced(dragPreview.to.clauseIndex, 0, {
      clause: dragPreview.clause,
      clauseName: `groups.${dragPreview.from.groupIndex}.clauses.${dragPreview.from.clauseIndex}`,
      formIndex: null,
    });
  }
  return formClauses;
};

// Renders the junction between groups, editable only above the second one.
const GroupJunction: React.FC<{
  control: Control<ClauseFormValues>;
  index: number;
  immutable: boolean;
  size: ClauseBlockSize;
}> = ({ control, index, immutable, size }) => (
  <div
    className="my-2 flex items-center gap-x-2"
    data-loc={`group-${index}-junction`}
  >
    <Controller
      control={control}
      name="junction"
      render={({ field }) =>
        index === 1 ? (
          <JunctionDropdown
            disabled={immutable}
            size={size}
            value={field.value}
            onChange={field.onChange}
          />
        ) : (
          <div
            className={twJoin(
              "flex h-8 shrink-0 items-center pl-2.5 font-inter-medium-12px text-gray-700",
              CLAUSE_BLOCK_SIZES[size].junctionWidth,
            )}
          >
            {field.value}
          </div>
        )
      }
    />
    <div className="h-px flex-1 bg-gray-200" />
  </div>
);

export const ClauseGroupRow: React.FC<Props> = ({
  index,
  isLoneGroup,
  onDelete,
  dragPreview,
  fieldErrors,
  hideActionButtons,
  ...rowProps
}) => {
  const { allowReordering, control, immutable, size } = rowProps;
  const { fields, append, remove } = useFieldArray({
    control,
    name: `groups.${index}.clauses`,
  });
  const groupName = useWatch({ control, name: `groups.${index}.name` });

  const displayClauses = toDisplayClauses(fields, index, dragPreview);
  // Makes the whole group body a drop target, so clauses can be dropped past
  // the last row (append).
  const { setNodeRef } = useDroppable({
    data: { groupIndex: index } satisfies GroupDropData,
    disabled: immutable,
    id: `clause-group-${index}`,
  });

  const rows = (
    <>
      <SortableContext
        disabled={immutable}
        items={displayClauses.map(({ clause }) => clause.id_left)}
      >
        {displayClauses.map(
          ({ clause, clauseName, formIndex }, displayIndex) => (
            <ClauseRow
              key={clause.id_left}
              {...rowProps}
              allowDeletingAllClauses={
                !isLoneGroup || rowProps.allowDeletingAllClauses
              }
              clauseName={clauseName}
              errorLeft={fieldErrors?.[clause.id_left]}
              errorRight={fieldErrors?.[clause.id_right]}
              idLeft={clause.id_left}
              idRight={clause.id_right}
              index={displayIndex}
              junctionName={`groups.${index}.junction`}
              length={displayClauses.length}
              sortableId={allowReordering ? clause.id_left : undefined}
              onDeleteClause={() => {
                if (formIndex === null) {
                  return;
                }
                if (!isLoneGroup && fields.length === 1) {
                  // Deleting a group's last condition deletes the group with it
                  onDelete();
                  return;
                }
                remove(formIndex);
              }}
            />
          ),
        )}
      </SortableContext>
      {!hideActionButtons && (
        <AddConditionButton
          immutable={immutable}
          onClick={() => append(getNewClause())}
        />
      )}
    </>
  );

  if (isLoneGroup) {
    return (
      <div ref={setNodeRef} data-loc={`condition-group-${index}`}>
        {rows}
      </div>
    );
  }

  return (
    <>
      {index > 0 && (
        <GroupJunction
          control={control}
          immutable={immutable}
          index={index}
          size={size}
        />
      )}
      <div data-loc={`condition-group-${index}`}>
        <div className="mb-2 font-inter-medium-11px text-gray-500 uppercase">
          {groupName ?? `Group ${index + 1}`}
        </div>
        <div className="flex gap-x-2.5">
          <div className="w-1.5 shrink-0 border-gray-200 border-l-2" />
          <div ref={setNodeRef} className="min-w-0 flex-1">
            {rows}
          </div>
        </div>
      </div>
    </>
  );
};
